import React, { Component } from 'react'
import styles from './styles'
import { AddIcon, LogoutIcon, PlayIcon, SearchIcon, CaretBackIcon, RemovePillIcon } from 'pdc-svg-icons'
import { AddCircleIcon } from 'svg-icons'
import { withStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import { ThemeProvider } from 'providers'

class ButtonBase extends Component {
    getButtonClasses (color, variant, shape) {
        const { classes, disabled, icon, iconPlacement } = this.props

        let buttonColorClass = `${variant}-button ${color}-${variant}-button`
        if (disabled) buttonColorClass += ' disabled'

        if (variant === 'graphic') {
            buttonColorClass += ' graphic-button'
            if (typeof (icon) === 'string') buttonColorClass += ` ${icon}-graphic-button ${color}-graphic-button`
            if (shape === 'circle') buttonColorClass += ` ${shape}-graphic-button`
        }

        const iconSpaceClass = icon ? `icon-space-${iconPlacement || 'left'}` : ''

        const classNames = `${classes.pdcButton} ${buttonColorClass} ${this.props.className} ${iconSpaceClass}`

        return classNames
    }

    getIcon = () => {
        const mappedIcons = {
            add: AddIcon,
            add_circle: AddCircleIcon,
            logout: LogoutIcon,
            search: SearchIcon,
            play: PlayIcon,
            back: CaretBackIcon,
            next: CaretBackIcon,
            remove: RemovePillIcon
        }

        let Icon = null

        const { icon, iconPlacement } = this.props
        if (icon) Icon = typeof (icon) === 'string' ? mappedIcons[icon] : icon

        const graphic = this.props.graphic
        if (graphic) Icon = mappedIcons[graphic]

        if (!Icon) return null
        const rotateClass = icon === 'next' ? 'rotate' : ''
        const iconPlacementClass = iconPlacement === 'right' ? 'icon-right' : 'icon-left'
        return <div className={`pdc-button-icon ${iconPlacementClass} ${rotateClass}`}><Icon color={this.props.color}/></div>
    }

    onKeyDown = e => {
        if (e.keyCode === 32 /* Space */ || e.keyCode === 13 /* Enter */) {
            e.preventDefault()
        }
    }

    onKeyUp = e => {
        if (e.keyCode === 32 /* Space */ || e.keyCode === 13 /* Enter */) {
            e.preventDefault()
            // eslint-disable-next-line no-unused-expressions
            this.props.onClick?.(e)
        }
    }

    render = () => {
        let { variant, color, iconPlacement, shape } = this.props
        iconPlacement = iconPlacement || 'left'
        variant = variant || 'filled'
        color = color || 'primary'
        shape = shape || 'elongated'
        const Icon = this.getIcon()
        const classNames = this.getButtonClasses(color, variant, shape)

        const buttonElementProps = ['id', 'disabled', 'onClick', 'onKeyDown', 'onKeyUp', 'onBlur', 'onDragLeave', 'onFocus', 'onBlur', 'onTouchEnd', 'onTouchMove', 'onTouchStart', 'onMouseLeave', 'onMouseDown', 'onMouseEnter']
        const propsToPass = {}
        Object.keys(this.props).forEach(propName => {
            if (['data-', 'aria-'].some(prefix => propName.startsWith(prefix)) || buttonElementProps.includes(propName)) {
                propsToPass[propName] = this.props[propName]
            }
        })

        return (
            <button
                {...propsToPass}
                className = {classNames}
                tabIndex = {this.props.tabIndex || 0}
                onKeyUp = {this.onKeyUp}
                onKeyDown = {this.onKeyDown}
                ref = {this.props.buttonRef}
            >
                {iconPlacement === 'left' ? Icon : null}
                {this.props.children}
                {iconPlacement === 'right' ? Icon : null}
            </button>
        )
    }
}

ButtonBase.propTypes = {
    /**
     * Material ui classes
     */
    classes: PropTypes.any,
    /**
     * Custom class
     */
    className: PropTypes.string,
    /**
     * The variant of the button
     * 'filled', 'outlined', 'graphic' or 'text'
     */
    variant: PropTypes.string,
    /**
     * The color of the button
     * 'primary', 'secondary', 'tertiary', 'attention' or 'important'
     */
    color: PropTypes.string,
    /**
     * Icon component that will be placed at the beginning of the button.
     * If string provided then it can be 'add', 'add_cicle', 'logout', 'search', 'play', 'back', 'remove' or 'next';
     * Otherwise it should be a pdc svg component.
     */
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * Where should the icon (if provided) be placed. Left or right?
     */
    iconPlacement: PropTypes.oneOf(['left', 'right']),
    /**
     * Is it disabled
     * true or false
     */
    shape: PropTypes.oneOf(['elongated', 'circle']),
    disabled: PropTypes.bool,
    /**
     * One of add, logout, search, play, back, next
     */
    graphic: PropTypes.oneOf(['add', 'logout', 'search', 'play', 'back', 'next']),
    /**
     * Function that should be called on click
     */
    onClick: PropTypes.func,
    /**
     * React children
     */
    children: PropTypes.any,
    buttonRef: PropTypes.any,

    tabIndex: PropTypes.number
}

ButtonBase.defaultProps = {
    variant: 'filled',
    color: 'primary',
    icon: null,
    iconPlacement: 'left',
    shape: 'elongated',
    disabled: false
}

const ButtonWithStyles = withStyles(styles)(ButtonBase)

/**
 * Button design component
 * @param {object} props
 */
const Button = props => (
    <ThemeProvider>
        <ButtonWithStyles {...props} />
    </ThemeProvider>
)

export default Button
